import { Moment } from 'moment';
import Permission from '../constants/Permission';
import { isPresent, Nullish } from '../utils/typescript';
import Person, { PersonLike } from './Person';
import { Url } from './common';

export interface UserId {__userId: number}

export interface UserLike extends PersonLike<UserId> {
  name: string | null;
}

export default class User extends Person<UserId> implements UserLike {
  name: string | null = null;
  phone: string | null = null;
  secondPhone: string | null = null;
  isActive = true;
  isDeleted = false;
  culture: string | null = null;
  hasPIN = false;
  gender: string | null = null;
  createdOn: Moment | null = null;
  creator: User | null = null;
  permissions: Array<Permission> = [];

  static withId(id: Nullish<UserId>): User | null {
    if (!isPresent(id)) return null;
    return User.withData({ id });
  }

  static withFullName(fullName: Nullish<string>): User | null {
    if (!fullName) return null;
    return User.withData({ fullName });
  }

  static withFullNameAndAvatar(fullName: Nullish<string>, avatar: Nullish<Url>): User | null {
    if (!fullName && !avatar) return null;
    return User.withData({ fullName: fullName ?? '', avatar: avatar ?? null });
  }

  static withIdAndFullName(id: Nullish<UserId>, fullName: Nullish<string>): User | null {
    if (!isPresent(id)) return null;
    return User.withData({ id, fullName: fullName ?? '' });
  }

  static withIdFullNameAndAvatar(id: Nullish<UserId>, fullName: Nullish<string>, avatar: Nullish<Url>): User | null {
    if (!isPresent(id)) return null;
    return User.withData({ id, fullName: fullName ?? '', avatar: avatar ?? null });
  }

  static withData(data: Partial<User>): User {
    return Object.assign(new User(), data);
  }

  set fullName(value: string) {
    super.fullName = value;
  }

  get fullName(): string {
    return super.fullName || this.name || '';
  }

  hasPermission(permission: Permission): boolean {
    return this.permissions.includes(permission);
  }

  hasAllPermissions(...permissions: Array<Permission>): boolean {
    return permissions.every(p => this.hasPermission(p));
  }

  hasAnyPermission(...permissions: Array<Permission>): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  canEditJobs(): boolean {
    return this.hasAnyPermission(Permission.FRONTDESK, Permission.CAN_EDIT_REPAIR_ORDER);
  }

  canCreateJobs(): boolean {
    return this.canEditJobs() || this.hasPermission(Permission.CAN_ADD_JOB);
  }

  canModifyDossier(): boolean {
    return this.hasPermission(Permission.ACCESS_TO_DOSSIERS) && this.hasAnyPermission(Permission.CAN_CHECKOUT, Permission.FRONTDESK);
  }
}
